/**
 * Couche de verbalisation partagée — transforme une valeur brute en mot
 * ou en fragment réutilisable par les articles de prose (Société, Économie,
 * Diplomatie) et la carte de profil. S'appuie sur le registre d'indicateurs
 * (source unique des seuils/verdicts) plutôt que de redéfinir des paliers.
 */

import { INDICATORS, type Verdict } from "./indicators";
import type { DerivedAggregates, WorldState } from "./types";

/** Forme minuscule, ancrable derrière « un niveau … » (toujours masculin). */
const VERDICT_ADJ: Record<Verdict, string> = {
  excellent: "excellent",
  sain: "sain",
  tenable: "tenable",
  preoccupant: "préoccupant",
  critique: "critique",
};

function defOf(id: string) {
  return INDICATORS.find((d) => d.id === id);
}

/** Valeur brute d'un indicateur (via son extracteur). */
export function indicatorValue(
  id: string,
  state: WorldState,
  agg: DerivedAggregates,
): number | null {
  const d = defOf(id);
  return d ? d.extract(state, agg) : null;
}

/** Valeur formatée + unité, telle qu'affichée dans le bandeau (« 5.7 % »). */
export function indicatorText(
  id: string,
  state: WorldState,
  agg: DerivedAggregates,
): string | null {
  const d = defOf(id);
  if (!d) return null;
  const v = d.extract(state, agg);
  return `${d.format(v)}${d.unit}`;
}

/** Verdict qualitatif minuscule (« préoccupant ») ou null si pas de paliers. */
export function verdictAdjective(
  id: string,
  state: WorldState,
  agg: DerivedAggregates,
): string | null {
  const d = defOf(id);
  if (!d || !d.verdict) return null;
  return VERDICT_ADJ[d.verdict(d.extract(state, agg))];
}
